/**
 * i18n maison. Dix-sept catalogues de ~4 Ko, charges A LA DEMANDE (cf. LOADERS
 * plus bas) : une lib de traduction ne se justifierait pas (cf. CLAUDE.md, pas
 * de dependance sans raison ecrite).
 *
 * Cles en anglais, FR est la reference : `TranslationKey` en est derive, donc
 * inventer une cle ne compile pas. La couverture de chaque catalogue, elle, est
 * verifiee par `locales.test.ts` depuis le passage a l'import dynamique.
 *
 * Pas de forme plurielle : le moteur n'interpole que `{nom}`. Les langues a
 * accord complexe (russe, polonais, tcheque, roumain) formulent donc les
 * comptes en « etiquette : {n} », qui reste juste quel que soit le nombre,
 * plutot qu'en « {n} manches », qui serait faux la plupart du temps. Le
 * hongrois et le vietnamien n'en ont pas besoin : le premier met le nom au
 * singulier apres un chiffre, le second ne decline pas. Le japonais et le
 * coreen non plus : ni l'un ni l'autre n'accorde le nom au nombre, un compteur
 * suffit.
 *
 * Japonais en です/ます, coreen en 해요체 : le registre poli ordinaire, celui
 * d'un jeu qui s'adresse a un inconnu sans raideur. Ces deux langues n'ont pas
 * de police telechargee, elles tombent sur la pile systeme (cf. tokens.css).
 */
import fr from './fr.json';

export const LANGUAGES = [
  'fr',
  'en',
  'es',
  'pt',
  'de',
  'it',
  'nl',
  'pl',
  'ro',
  'cs',
  'hu',
  'tr',
  'ru',
  'vi',
  'id',
  'ja',
  'ko',
] as const;
export type Language = (typeof LANGUAGES)[number];

export type TranslationKey = keyof typeof fr;

/**
 * Nom de chaque langue DANS cette langue.
 *
 * Un endonyme, jamais un drapeau : un drapeau designe un pays, pas une langue.
 * L'espagnol n'est pas espagnol, le portugais est surtout bresilien, et le
 * russe se parle bien au-dela de la Russie. Ecrire « Portugues » se lit sans
 * ambiguite par la personne concernee, qui est la seule a devoir le lire.
 */
export const LANGUAGE_NAMES: Record<Language, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  pt: 'Português',
  de: 'Deutsch',
  tr: 'Türkçe',
  pl: 'Polski',
  ru: 'Русский',
  it: 'Italiano',
  nl: 'Nederlands',
  vi: 'Tiếng Việt',
  cs: 'Čeština',
  ro: 'Română',
  hu: 'Magyar',
  id: 'Bahasa Indonesia',
  ja: '日本語',
  ko: '한국어',
};

/**
 * Chargement A LA DEMANDE.
 *
 * Les catalogues pesaient 177 Ko bruts dans le bundle initial, pour quatorze
 * langues qu'un joueur donne ne lira jamais. Chacun part desormais dans son
 * propre fragment, telecharge quand la langue est choisie.
 *
 * FR reste STATIQUE, et c'est ce qui rend l'operation indolore : il sert de
 * repli, donc le premier rendu a toujours quelque chose a afficher. Un joueur
 * dont la langue n'est pas encore arrivee voit le francais pendant une image ou
 * deux, jamais un ecran vide.
 *
 * CONTREPARTIE ASSUMEE : le `satisfies` qui verifiait a la COMPILATION que
 * chaque catalogue couvre exactement les cles de FR ne s'applique plus a un
 * import dynamique. Cette garantie vit maintenant dans `locales.test.ts`, qui
 * compare les jeux de cles de tous les catalogues. Ne pas supprimer ce test.
 */
const LOADERS: Record<Language, () => Promise<{ default: Record<string, string> }>> = {
  fr: async () => ({ default: fr }),
  en: () => import('./en.json'),
  es: () => import('./es.json'),
  pt: () => import('./pt.json'),
  de: () => import('./de.json'),
  it: () => import('./it.json'),
  nl: () => import('./nl.json'),
  pl: () => import('./pl.json'),
  ro: () => import('./ro.json'),
  cs: () => import('./cs.json'),
  hu: () => import('./hu.json'),
  tr: () => import('./tr.json'),
  ru: () => import('./ru.json'),
  vi: () => import('./vi.json'),
  id: () => import('./id.json'),
  ja: () => import('./ja.json'),
  ko: () => import('./ko.json'),
};

const loaded = new Map<Language, Record<string, string>>([['fr', fr]]);
const listeners = new Set<() => void>();

/** Previent les composants qu'un catalogue vient d'arriver. */
export function onCatalogLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Telecharge le catalogue d'une langue s'il manque. Idempotent : deux appels
 * simultanes ne declenchent qu'un seul telechargement, `import()` mettant sa
 * promesse en cache.
 */
export function ensureCatalog(language: Language): void {
  if (loaded.has(language)) return;
  void LOADERS[language]()
    .then((module) => {
      loaded.set(language, module.default);
      for (const listener of listeners) listener();
    })
    // Un fragment qui ne se charge pas laisse le francais a l'ecran : c'est
    // laid, mais le jeu reste jouable.
    .catch(() => undefined);
}

export type TranslateParams = Record<string, string | number>;

export const DEFAULT_LANGUAGE: Language = 'fr';

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Langue deduite du navigateur, utilisee tant que le joueur n'a pas choisi
 * explicitement. `navigator.languages` est teste avant `navigator.language`
 * pour respecter l'ordre de preference reel de l'utilisateur.
 *
 * On ne compare que la sous-etiquette primaire : `pt-BR` et `pt-PT` tombent
 * tous deux sur `pt`, `es-419` sur `es`. Le catalogue unique par langue est
 * ecrit pour rester lisible de part et d'autre.
 */
export function detectLanguage(): Language {
  if (typeof navigator === 'undefined') return DEFAULT_LANGUAGE;

  const candidates = [...(navigator.languages ?? []), navigator.language].filter(
    (tag): tag is string => typeof tag === 'string',
  );

  for (const tag of candidates) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLanguage(base)) return base;
  }
  return DEFAULT_LANGUAGE;
}

/** Interpolation `{nom}`. Un placeholder sans valeur est laisse tel quel. */
export function translate(
  language: Language,
  key: TranslationKey,
  params?: TranslateParams,
): string {
  const template = loaded.get(language)?.[key] ?? fr[key] ?? key;
  if (params === undefined) return template;

  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}
